<template>
    <q-dialog
        v-model="show"
        transition-show="flip-down"
        transition-hide="flip-up"
        @hide="$emit('close')"
    >
        <q-card class="text-white" style="width: 90%">
            <!-- Bar strip -->
            <q-bar class="bg-primary" style="height: 35px">
                <div>{{ $t('Start a changeover') }}</div>

                <q-space />

                <q-btn @click="$emit('close')" dense flat icon="close">
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>
            <div>
                <!-- Body -->
                <q-card-section class="q-px-lg q-py-none text-black">
                    <div
                        class="row q-mb-md"
                        v-if="
                            amendRecentDowntime &&
                            amendRecentDowntimeFeatureEnabled &&
                            form.cause &&
                            currentDowntimeExceededCauseTargetedDowntime(
                                form.cause.target_down_time
                            )
                        "
                    >
                        <q-banner
                            class="background-yellow border-yellow q-mt-sm"
                            inline-actions
                            rounded
                        >
                            <template v-slot:avatar>
                                <q-icon
                                    name="fa-solid fa-triangle-exclamation"
                                    color="warning"
                                ></q-icon>
                            </template>
                            {{
                                $t(
                                    'The current downtime phase has exceeded the expected downtime duration of the selected cause. This changeover will be recorded, but the asset will remain in downtime.'
                                )
                            }}
                        </q-banner>
                    </div>
                    <div class="q-mt-md">
                        <planned-shutdown-banner :show="isPlannedShutdown" />

                        {{ $t('Select a cause for this changeover') }}
                    </div>

                    <q-form @submit="submit">
                        <!-- Cause selector -->
                        <table-select
                            id="changeover-cause-select"
                            v-model="form.cause"
                            @confirmSelection="
                                form.cause = causeSelectProps.selection
                            "
                            :baseURL="causeSelectProps.url"
                            :queryParams="causeSelectProps.queryParams"
                            :label="causeSelectProps.label"
                            :dialogTitle="causeSelectProps.dialogTitle"
                            :searchLabel="causeSelectProps.searchLabel"
                            :dense="true"
                            :rules="[val => !!val || $t('Select a cause')]"
                            row-key="id"
                        >
                            <template v-slot:item="data">
                                <cause-card
                                    @select="
                                        selectCause(
                                            data.props.row,
                                            causeSelectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :lossType="data.props.row.type_of_loss"
                                    :categoryName="data.props.row.category_name"
                                    :planned="data.props.row.planned"
                                    :description="data.props.row.description"
                                    :targetDowntime="
                                        data.props.row.target_down_time
                                    "
                                    :isSelected="
                                        causeSelectProps.selection &&
                                        causeSelectProps.selection.id ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                    :equipmentFailure="
                                        data.props.row.equipment_failure
                                    "
                                ></cause-card>
                            </template>

                            <template v-slot:add-option>
                                <q-btn
                                    @click="
                                        $router.push({
                                            name: 'cause_create'
                                        })
                                    "
                                    icon="add"
                                    color="secondary"
                                    size="md"
                                    round
                                    dense
                                >
                                    <q-tooltip>{{ $t('Add cause') }}</q-tooltip>
                                </q-btn>
                            </template>
                        </table-select>
                        <div v-if="form.cause" class="q-mb-md">
                            <span
                                ><b>{{ $t('Expected downtime') + ':' }}</b>
                                {{
                                    `${form.cause.target_down_time} ${$t(
                                        'minutes'
                                    )}`
                                }}
                            </span>
                        </div>

                        <div class="row q-mb-md" v-if="hasCurrentProduct">
                            <q-checkbox
                                id="keep-current-product-checkbox"
                                :model-value="keepCurrentProduct"
                                @update:model-value="
                                    val => {
                                        keepCurrentProduct = val;
                                        form.product = {
                                            value: currentProduct.id,
                                            label: currentProduct.name
                                        };
                                        productSelectProps.selection =
                                            form.product;
                                    }
                                "
                                :label="$t('Keep running the same product')"
                            />
                        </div>
                        <div>
                            {{ $t('Select the next product') }}
                        </div>

                        <table-select
                            id="changeover-product-select"
                            v-model="form.product"
                            @confirmSelection="
                                form.product = productSelectProps.selection
                            "
                            :baseURL="productSelectProps.url"
                            :queryParams="productSelectProps.queryParams"
                            :label="productSelectProps.label"
                            :dialogTitle="productSelectProps.dialogTitle"
                            :searchLabel="productSelectProps.searchLabel"
                            :dense="true"
                            :rules="[val => !!val || $t('Select a product')]"
                            row-key="id"
                            :disable="keepCurrentProduct"
                        >
                            <template v-slot:item="data">
                                <product-card
                                    @select="
                                        productSelectProps.selection = {
                                            label: data.props.row.name,
                                            value: data.props.row.product
                                        }
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :expected-run-rate="
                                        data.props.row.expected_run_rate
                                    "
                                    :unit-measure="data.props.row.unit_measure"
                                    :unit-packing="data.props.row.unit_packing"
                                    :isSelected="
                                        productSelectProps.selection &&
                                        productSelectProps.selection.value ===
                                            data.props.row.product
                                    "
                                    :isSelectable="true"
                                ></product-card>
                            </template>
                        </table-select>

                        <div
                            v-if="
                                ongoingDowntime &&
                                amendRecentDowntimeFeatureEnabled
                            "
                        >
                            <div class="row q-mb-md">
                                <div class="row">
                                    {{
                                        $t(
                                            'The line is currently down. Do you want to adjust the timeline and include the current downtime as a part of this changeover?'
                                        )
                                    }}
                                    <div class="row q-mt-sm">
                                        <q-radio
                                            v-model="amendRecentDowntime"
                                            :val="true"
                                            class="q-mr-xl"
                                            :label="$t('Yes')"
                                            :style="
                                                $q.screen.lt.sm
                                                    ? ''
                                                    : 'padding-right: 5rem'
                                            "
                                            color="accent"
                                        />
                                        <q-radio
                                            v-model="amendRecentDowntime"
                                            :val="false"
                                            class="q-mr-xl"
                                            :label="$t('No')"
                                            color="accent"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div align="right" class="q-pb-md">
                            <q-btn
                                id="cancel-changeover-button"
                                @click="$emit('close')"
                                class="q-mr-md"
                                color="white"
                                text-color="primary"
                                outline
                                >{{ $t('Cancel') }}</q-btn
                            >
                            <q-btn
                                id="submit-changeover-button"
                                :loading="isLoading"
                                type="submit"
                                color="primary"
                                >{{ $t('Submit') }}</q-btn
                            >
                        </div>
                    </q-form>
                </q-card-section>
            </div>
        </q-card>
    </q-dialog>
</template>

<script>
import CauseCard from '@components/CauseCard.vue';
import PlannedShutdownBanner from '@components/PlannedShutdownBanner.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import { mapActions } from 'pinia';
import { useProductsStore } from '@stores/products.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';

export default {
    name: 'ChangeoverDialog',
    components: {
        TableSelect,
        CauseCard,
        ProductCard,
        PlannedShutdownBanner
    },
    emits: ['close', 'startChangeover'],
    props: {
        assetId: {
            type: [Number, String],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        },
        currentProduct: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            form: {
                cause: null,
                product: null
            },
            show: true,
            isLoading: false,
            productURL: `assets/${this.assetId}/products/`,

            // cause selector
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                queryParams: { order: 'name', planned: true },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause for this changeover'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            productSelectProps: {
                url: `/assets/${this.assetId}/products/`,
                queryParams: { order: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product for this changeover'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            amendRecentDowntime: false,
            keepCurrentProduct: false
        };
    },
    computed: {
        /**
         * Changeover payload.
         */
        payload() {
            const payload = {
                phase: 'changeover',
                cause: null,
                product: null,
                amend_recent_downtime: this.amendRecentDowntime
            };
            // check if values has been selected
            if (this.form.cause) {
                payload['cause'] = this.form.cause.id;
            }
            if (this.form.product) {
                payload['product'] = this.form.product.value;
            }
            return payload;
        },
        /**
         * Controls the PlannedShutdownBanner visibility by checking if
         * the type of loss for the selected cause is planned shutdown
         */
        isPlannedShutdown() {
            if (this.causeSelectProps.selection) {
                return (
                    this.causeSelectProps.selection.type_of_loss == 'shutdown'
                );
            }
            return false;
        },
        /**
         * Check if there is an ongoing downtime
         * @returns {boolean}
         * True if there is an ongoing downtime.
         * False if there is no ongoing downtime
         */
        ongoingDowntime() {
            let currentPhase = this.$store.getters['facilities/getAssetPhase'](
                this.assetId
            );
            return currentPhase && currentPhase.status === 'in_downtime';
        },
        /**
         * Helper computed property to check if the feature flag for
         * amending the recent ongoing downtime is enabled
         */
        amendRecentDowntimeFeatureEnabled() {
            return this.isFeatureEnabled('AMEND_RECENT_DOWNTIME');
        },
        /**
         * Check if there is a current product
         * @returns {boolean} - True if there is a current product.
         */
        hasCurrentProduct() {
            if (
                !this.currentProduct ||
                !this.currentProduct.id ||
                !this.currentProduct.name
            ) {
                return false;
            }
            return true;
        }
    },
    methods: {
        isFeatureEnabled,
        ...mapActions(useProductsStore, ['loadProductLinkAction']),
        /**
         * Handle submit
         */
        async submit() {
            this.isLoading = true;
            try {
                let response = await this.$axios.post(
                    `assets/${this.assetId}/phases/start`,
                    this.payload
                );
                let phase = response.data;
                // set asset phase
                this.$store.dispatch('facilities/setAssetPhase', {
                    id: phase.id,
                    phaseUUID: phase.uuid,
                    assetId: this.assetId,
                    status: phase.phase,
                    cause: phase.cause,
                    currentProduct: this.payload.product,
                    start: phase.start,
                    lockedTransitionToRunning:
                        phase.locked_transition_to_running
                });
                this.loadProductLinkAction({
                    facilityId: this.facilityId,
                    assetId: this.assetId,
                    productId: this.payload.product
                });
                this.$emit('startChangeover');
                this.$emit('close');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to start a switch phase. Please try again later',
                        error
                    )
                );
            } finally {
                this.isLoading = false;
            }
        },
        /**
         * Check if the current downtime has exceeded the targeted downtime of the selected cause
         * @param {number} causeTargetedDowntime - The targeted downtime of the selected cause
         * @returns {boolean}
         * True if the current downtime has exceeded the targeted downtime of the selected cause.
         * False if the current downtime has not exceeded the targeted downtime of the selected cause
         */
        currentDowntimeExceededCauseTargetedDowntime(causeTargetedDowntime) {
            let result = false;
            if (this.ongoingDowntime) {
                let currentDowntime = this.$store.getters[
                    'facilities/getAssetPhase'
                ](this.assetId);
                let currentDowntimeDurationMin =
                    (new Date().getTime() -
                        new Date(currentDowntime.start).getTime()) /
                    60000;
                result = currentDowntimeDurationMin > causeTargetedDowntime;
            }
            return result;
        },
        /**
         * Select/deselect causes in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props
         */
        selectCause(row, props) {
            if (props.selection && props.selection.id === row.id) {
                props.selection = null;
            } else {
                props.selection = row;
            }
        }
    }
};
</script>

<style lang="scss"></style>
